import { type AxiosError } from "axios";
import SingleSignIn from "components/custom/auth/SingleSignIn";
import MainLayout from "layouts/Main";
import OnboardLayout from "layouts/Onboard";
import PassSignIn from "modules/auth/PassSignIn";
import { Provider } from "react-redux";
import { Router, Route, Switch } from "react-router-dom";
import { updateAmzOnboardingStatus } from "redux/reducers/amzOnboardReducer";
import { logoutUser, setCurrentUser } from "redux/reducers/userReducer";
import { setAuthLoading } from "redux/reducers/authReducers";
import { setErrors } from "redux/reducers/errorReducers";
import { setMarketPlace } from "redux/reducers/marketPlace";
import store from "redux/store";
import setAuthToken from "utils/setAuthToken";
import { setChannel } from "redux/reducers/channel";
import { ThemeProvider } from "@material-ui/core";
import trellisTheme from "assets/themes/TrellisTheme";
import * as Sentry from "@sentry/react";
import { createBrowserHistory } from "history";
import ErrorComponent from "layouts/Error";
import signUplogo from "assets/images/logo/Trellis_Logo.png";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import NotificationProvider from "notifications/NotificationProvider";
import AdPlanProvider from "components/core/blocks/AdPlan/AdPlanProvider";
import Shopify from "./modules/shopify/Redirector";
import GoogleAnalytics from "components/custom/GoogleAnalytics";
import SignupLayout from "views/onboarding/layout/SignupLayout";
import MixpanelAnalytics from "components/custom/MixpanelAnalytics";
import PDFView from "views/PDFView";
import { FollowPixel } from "utils/followPixel";
import { getAuthTokens, setAuthTokens } from "./auth/tokens";
import { ReactQueryProvider } from "./config/react-query";
import NiceModal from "@ebay/nice-modal-react";
import { configureAxios } from "./config/axios";
import { css } from "../styled-system/css";
import { ToastContainer } from "react-toastify";
import { apiClient } from "./zodios/client";

declare global {
  interface Window {
    _hsq?: [string, unknown][];
  }
}

configureAxios();
const history = createBrowserHistory();

Sentry.init({
  dsn:
    import.meta.env.PROD ?
      "https://9514d9f62a744b1cbc89b3e83bfd672d@o944803.ingest.sentry.io/5893305"
    : import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [Sentry.reactRouterV5BrowserTracingIntegration({ history }), Sentry.replayIntegration()],
  // only sample 10% of transactions for performance metrics
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  _experiments: { enableLogs: true },
  ignoreErrors: ["Request failed with status code 401"],
});

const tokens = getAuthTokens();
// Check for token to keep user logged in
if (tokens) {
  store.dispatch(setAuthLoading(true));
  setAuthTokens(tokens);

  apiClient
    .getUserProfile()
    .then((res) => {
      let defaultMarketplace = "USA";
      const orgId = res.organization_id;
      if (orgId) {
        defaultMarketplace = res.default_marketplace?.[orgId] || "USA";
      }
      const marketPlace =
        localStorage.Marketplace && localStorage.Marketplace !== "undefined" ?
          localStorage.Marketplace
        : defaultMarketplace;
      const amzOnboardingStatus = JSON.parse(localStorage.AmzOnboard ?? "{}");
      const channel = localStorage.channel;

      store.dispatch(setCurrentUser(res));
      // Assign HubSpot email
      window._hsq = window._hsq || [];
      window._hsq.push([
        "identify",
        {
          email: res.email,
        },
      ]);

      if (
        marketPlace === "CA" ||
        marketPlace === "USA" ||
        marketPlace === "MX" ||
        marketPlace === "UK" ||
        marketPlace === "NL" ||
        marketPlace === "ES" ||
        marketPlace === "IT" ||
        marketPlace === "FR" ||
        marketPlace === "BR" ||
        marketPlace === "IN" ||
        marketPlace === "AE" ||
        marketPlace === "DE" ||
        marketPlace === "JP" ||
        marketPlace === "SG" ||
        marketPlace === "AU" ||
        marketPlace === "SE" ||
        marketPlace === "PL" ||
        marketPlace === "BE" ||
        marketPlace === "SA" ||
        marketPlace === "TR" ||
        marketPlace === "IE"
      ) {
        localStorage.Marketplace = marketPlace;
        store.dispatch(setMarketPlace(marketPlace));
      } else {
        localStorage.Marketplace = "USA";
        store.dispatch(setMarketPlace("USA"));
      }

      if (channel === "amazon" || channel === "google" || channel === "walmart" || channel === "shopify") {
        store.dispatch(setChannel(channel));
      } else {
        throw new Error(`Invalid channel ${channel}.`);
      }

      // TODO - zod schema validation
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      store.dispatch(updateAmzOnboardingStatus(amzOnboardingStatus as any));

      setAuthToken(tokens.access_token);

      store.dispatch(setAuthLoading(false));
    })
    .catch((err: AxiosError) => {
      if (err.response && err.response.status === 401) {
        // Unauthorized, redirect to login
      } else {
        // Unexpected error send to sentry
        Sentry.captureException(err);
      }
      const usererr = {
        message: "Your session has expired. Please login again.",
      };
      store.dispatch(setErrors(usererr));
      store.dispatch(logoutUser());
    });
}

const App = () => {
  if (import.meta.env.VITE_MAINTENANCE === "true") {
    return (
      <div className="container">
        <div className="row">
          <div className="col-12 text-center" style={{ margin: "4em" }}>
            <img className="" style={{ width: "50%" }} src={signUplogo} alt="Trellis" />
          </div>
        </div>
        <div className="row">
          <div className="col-12 text-center">
            <h1 style={{ fontSize: "2em" }}>The portal is currently down for maintenance</h1>
            <p>
              If you require immediate assistance please reach out to <a href="mailto:support@gotrellis.com">support</a>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <ThemeProvider theme={trellisTheme}>
        <Sentry.ErrorBoundary fallback={<ErrorComponent />}>
          <ReactQueryProvider>
            <NotificationProvider>
              <NiceModal.Provider>
                <AdPlanProvider>
                  <Router history={history}>
                    <FollowPixel />
                    <Switch>
                      <Route path="/pdf">
                        <PDFView />
                      </Route>
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      <Route path="/user" render={(props) => <MainLayout {...(props as any)} />} />
                      <Route path="/onboard" render={(props) => <OnboardLayout {...props} />} />
                      <Route path="/onboarding/v2/">
                        <SignupLayout />
                      </Route>

                      <Route path="/referral/:referraltoken">
                        <SingleSignIn />
                      </Route>
                      <Route path="/affiliate/:affiliatetoken">
                        <SingleSignIn />
                      </Route>
                      <Route path="/signin">
                        <PassSignIn />
                      </Route>
                      <Route path={"/shopify"}>
                        <Shopify />
                      </Route>
                      <Route path="/">
                        <SingleSignIn />
                      </Route>
                    </Switch>
                    <GoogleAnalytics />
                    <MixpanelAnalytics />
                  </Router>
                  <div className={css({ fontSize: "22px" })}>
                    <ReactQueryDevtools initialIsOpen={false} buttonPosition={"bottom-left"} position="right" />
                  </div>
                </AdPlanProvider>
                <ToastContainer position={"top-right"} />
              </NiceModal.Provider>
            </NotificationProvider>
          </ReactQueryProvider>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </Provider>
  );
};

export default Sentry.withProfiler(App);
